<template>
  <div>
    <div v-if="showCreateProduct">
      <div class="create-product-container__return-button">
        <a-icon
          type="arrow-left"
          style="cursor: pointer"
          @click="closeProductForm"
        />
        {{ $t("support.product_create_subject") }}
      </div>
      <ProductDraftEditor
        ref="productEditorRef"
        :showValidation="someRequiredAttrsEmpty"
        @change="onProductFormChanged"
      />

      <div style="display: flex; justify-content: flex-end">
        <a-button
          type="primary"
          icon="plus"
          :disabled="!draftId"
          :loading="isFetching"
          @click="handleCompleteDraft"
        >
          {{ $t("common.create") }}
        </a-button>
      </div>
    </div>

    <div v-else-if="showProductDetails">
      <ExistedProductDetails
        :choosenExistingProduct="productDetails"
        @closeProductDetails="onHideProductDetails"
        @selectExistedProduct="handleSelectProduct"
      />
    </div>

    <div v-show="!showCreateProduct && !showProductDetails">
      <ChooseExistingProduct
        showCategories
        @selectExistedProduct="handleSelectProduct"
        @showDetails="onShowProductDetails"
      />

      <div class="create-product-container">
        <a-alert
          showIcon
          :message="$t('product_catalog.application_for_adding_goods')"
        />

        <a-button
          type="primary"
          icon="plus"
          ghost
          @click="openProductCreation"
        >
          {{ $t("product_catalog.add_product") }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { message } from "ant-design-vue"
import { ref } from "vue"

import i18n from "@/i18n"
import ProductDraftEditor from "@/modules/offers/_components/CreateOffer/components/ProductEditor/ProductDraftEditor.vue"
import { formatAttributesForRequest } from "@/modules/offers/_components/CreateOffer/utils/productAttributes"
import type { ProductForm as ProductFormValues } from "@/modules/offers/types/offers"
import {
  ChooseExistingProduct,
  ExistedProductDetails
} from "@/modules/sync/_components/ExistedProducts"
import * as productCatalogService from "@/services/productCatalogService"

export type SearchedProduct = {
  pCode: string
  title: string
  images: string[]
}

type Emits = {
  (event: "selected", product: SearchedProduct): void
  (event: "close"): void
}

const emit = defineEmits<Emits>()

const productEditorRef = ref()
const showCreateProduct = ref<boolean>(false)
const isFetching = ref<boolean>(false)
const draftId = ref<string>()
const productDetails = ref()
const showProductDetails = ref<boolean>(false)
const someRequiredAttrsEmpty = ref<boolean>(false)

const handleSelectProduct = (product) => {
  emit("selected", {
    pCode: product.p_code,
    title: product.name,
    images: product.images
  })
}
const openProductCreation = () => {
  showCreateProduct.value = true
}
const closeProductForm = () => {
  draftId.value = undefined
  showCreateProduct.value = false
}
const onProductFormChanged = async (payload: Partial<ProductFormValues>) => {
  try {
    isFetching.value = true

    if (!draftId.value) {
      const { data } = await productCatalogService.createProductDraft({ payload })
      draftId.value = data.oid
    }

    const { data } = await productCatalogService.updateProductDraft({
      payload,
      oid: draftId.value
    })

    if (payload.type || payload.type === null) {
      productEditorRef.value.setAttributes(data.attributes || [])
    }
  } finally {
    isFetching.value = false
  }
}
const handleCompleteDraft = async () => {
  const form = await productEditorRef.value.getFormValues()

  if (!form) return

  someRequiredAttrsEmpty.value = false

  const attributes = formatAttributesForRequest(form.attributes, form.free_form_attributes)

  if (
    attributes.some((attr) => {
      if (Array.isArray(attr.value) ? !attr.value.length : !attr.value) return attr.is_required

      return false
    })
  ) {
    message.error(i18n.t("offers.offer_form_required_attributes_error") as string)
    someRequiredAttrsEmpty.value = true
    return
  }

  try {
    isFetching.value = true

    const product = {
      attributes: attributes
        .filter((item) => item.value !== null)
        .map((item) => ({ code: item.code, value: item.value }))
    }

    await productCatalogService.updateProductDraft({
      payload: product,
      oid: draftId.value
    })

    const { data } = await productCatalogService.completeProductDraft({
      oid: draftId.value
    })

    handleSelectProduct({
      p_code: data.p_code,
      name: form.public_title_mlang[i18n.locale],
      images: form.images.map(({ filename }) => filename)
    })

    closeProductForm()
  } finally {
    isFetching.value = false
  }
}
const onShowProductDetails = (product) => {
  productDetails.value = product
  showProductDetails.value = true
}
const onHideProductDetails = () => {
  showProductDetails.value = false
  productDetails.value = undefined
}
</script>

<style lang="scss" scoped>
.create-product-container {
  padding: 16px;

  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 16px;

  &__return-button {
    display: flex;
    align-items: center;
    gap: 16px;

    font-size: 16px;
    height: 36px;
  }
}
</style>
